/**
 * One shot in a flythrough playlist.
 *
 * Reorderable by HTML5 drag-and-drop. The speed readout is the honest half of
 * the "per-waypoint duration" model: durations are the authoring control, so
 * neighbouring segments can legitimately move at different speeds. Showing the
 * number (and offering "Even pace" in the panel) is better than hiding it.
 */
import { useState } from 'react'
import { Eye, GripVertical, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FlythroughWaypoint } from './types'

const field =
  'w-full rounded border border-white/10 bg-black/40 px-1.5 py-0.5 text-[11px] text-white/90 ' +
  'tabular-nums outline-none focus:border-sky-400/50'
const microLabel = 'text-[9px] font-semibold uppercase tracking-wide text-white/35'

interface WaypointRowProps {
  waypoint: FlythroughWaypoint
  index: number
  /** Ground speed of the segment leaving this waypoint, mph. */
  speedMph: number | null
  /** True for the final waypoint of a non-looping tour (its duration is unused). */
  isTerminal: boolean
  isCurrent: boolean
  disabled: boolean
  onRename: (name: string) => void
  onChange: (patch: Partial<FlythroughWaypoint>) => void
  onPreview: () => void
  onDelete: () => void
  onDragStart: () => void
  onDrop: () => void
}

export function WaypointRow({
  waypoint,
  index,
  speedMph,
  isTerminal,
  isCurrent,
  disabled,
  onRename,
  onChange,
  onPreview,
  onDelete,
  onDragStart,
  onDrop,
}: WaypointRowProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(waypoint.name)
  const [dragOver, setDragOver] = useState(false)

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next && next !== waypoint.name) onRename(next)
    else setDraft(waypoint.name)
  }

  return (
    <div
      draggable={!disabled}
      onDragStart={onDragStart}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        onDrop()
      }}
      className={cn(
        'rounded-md border px-2 py-1.5 transition-colors',
        isCurrent
          ? 'border-sky-400/40 bg-sky-500/20'
          : 'border-white/10 bg-white/5 hover:bg-white/10',
        dragOver && 'border-sky-400/60'
      )}
    >
      <div className="flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-white/25" />
        <span className="w-4 shrink-0 text-[10px] tabular-nums text-white/30">{index + 1}</span>

        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              if (e.key === 'Escape') {
                setDraft(waypoint.name)
                setEditing(false)
              }
            }}
            className={cn(field, 'flex-1')}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex-1 truncate text-left text-[11px] font-medium text-white/85 hover:text-white"
            title="Click to rename"
          >
            {waypoint.name}
          </button>
        )}

        <button
          type="button"
          onClick={onPreview}
          disabled={disabled}
          title="Fly to this shot"
          className="shrink-0 rounded p-1 text-white/40 hover:bg-white/10 hover:text-white disabled:opacity-30"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          title="Delete shot"
          className="shrink-0 rounded p-1 text-white/40 hover:bg-rose-500/20 hover:text-rose-200 disabled:opacity-30"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-1 flex items-end gap-1.5 pl-[1.6rem]">
        <label className="flex-1">
          <span className={microLabel}>{isTerminal ? 'To first' : 'To next'}</span>
          <input
            type="number"
            min={0.1}
            step={0.5}
            value={(waypoint.durationMs / 1000).toFixed(1)}
            disabled={disabled}
            onChange={(e) =>
              onChange({ durationMs: Math.max(100, Number(e.target.value) * 1000) })
            }
            className={field}
            title={
              isTerminal
                ? 'Seconds from this shot back to the first — only used when Loop is on'
                : 'Seconds to travel from this shot to the next'
            }
          />
        </label>
        <label className="flex-1">
          <span className={microLabel}>Hold</span>
          <input
            type="number"
            min={0}
            step={0.5}
            value={(waypoint.holdMs / 1000).toFixed(1)}
            disabled={disabled}
            onChange={(e) => onChange({ holdMs: Math.max(0, Number(e.target.value) * 1000) })}
            className={field}
            title="Seconds to sit motionless on this shot before moving on"
          />
        </label>
        <div className="flex-1">
          <span className={microLabel}>Speed</span>
          <div
            className="truncate px-1.5 py-0.5 text-[11px] tabular-nums text-white/45"
            title="Ground speed of the move leaving this shot"
          >
            {speedMph == null ? '—' : `${Math.round(speedMph)} mph`}
          </div>
        </div>
      </div>
    </div>
  )
}
